import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CodeBlock from '../common/CodeBlock';

/**
 * A ```mermaid fence rendered as a diagram (design spec §12: a diagram is an
 * illustration, so the source stays reachable as the tabular/textual
 * alternative — here via "Quelltext anzeigen", never as the only fallback).
 *
 * Three things this component is careful about:
 *
 *  - **Bundle** — mermaid is ~500 kB of parser and layout engine. It is
 *    imported dynamically so it only loads for a thread that actually contains
 *    a diagram, and rollup gives it its own chunk.
 *  - **Trust** — the diagram source is model output. `securityLevel: 'strict'`
 *    keeps mermaid's sanitiser on and disables `click` directives; `htmlLabels:
 *    false` means labels render as SVG text rather than injected markup.
 *  - **Failure** — an LLM writes invalid mermaid often enough that a thrown
 *    parse error must not blank the answer. `mermaid.parse()` validates first,
 *    and anything that still fails degrades to the code block the reader would
 *    have got before.
 *
 * Theme follows `data-theme` on the root element, which ThemeContext keeps in
 * sync with the resolved Auto/Paper/Ink choice.
 */

let mermaidPromise = null;

/** One module-level import; every diagram on the page shares it. */
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((mod) => mod.default || mod);
  }
  return mermaidPromise;
}

function readTheme() {
  if (typeof document === 'undefined') return 'paper';
  return document.documentElement.dataset.theme === 'ink' ? 'ink' : 'paper';
}

/**
 * Mermaid ships its own palettes (a lavender `default`, a blue `dark`), which
 * read as a foreign object dropped into an Etch answer. Its `base` theme is the
 * one meant to be re-coloured, so the diagram is dressed in the semantic tokens
 * the rest of the surface uses and follows Paper/Ink for free.
 *
 * Reading the *computed* values rather than handing mermaid raw custom-property
 * references matters:
 * mermaid derives several colours arithmetically (khroma), which needs real
 * colour values, and the deliberately restrained palette keeps a diagram from
 * shouting louder than the prose around it. Only opaque tokens are used — the
 * `*-wash` tokens carry alpha, which those derivations mangle.
 */
function themeVariables() {
  const style = getComputedStyle(document.documentElement);
  const token = (name, fallback) => style.getPropertyValue(name).trim() || fallback;

  const canvas = token('--color-surface-raised', '#ffffff');
  const fill = token('--color-surface-sunken', '#eeeeee');
  const border = token('--color-border-strong', '#888888');
  const subtle = token('--color-border-subtle', '#cccccc');
  const text = token('--color-text-primary', '#111111');
  const muted = token('--color-text-secondary', '#555555');
  const line = token('--color-text-tertiary', '#777777');
  const danger = token('--color-danger', '#d60036');

  return {
    darkMode: readTheme() === 'ink',
    fontFamily: token('--font-sans', 'system-ui, sans-serif'),
    fontSize: token('--text-sm', '14px'),

    background: canvas,
    mainBkg: fill,
    primaryColor: fill,
    primaryTextColor: text,
    primaryBorderColor: border,
    secondaryColor: canvas,
    secondaryTextColor: text,
    secondaryBorderColor: subtle,
    tertiaryColor: canvas,
    tertiaryTextColor: muted,
    tertiaryBorderColor: subtle,

    textColor: text,
    titleColor: text,
    lineColor: line,
    nodeBorder: border,
    nodeTextColor: text,
    clusterBkg: canvas,
    clusterBorder: subtle,
    edgeLabelBackground: canvas,
    labelBackground: canvas,
    altBackground: canvas,

    // Sequence diagrams name every part separately instead of deriving them.
    actorBkg: fill,
    actorBorder: border,
    actorTextColor: text,
    actorLineColor: line,
    signalColor: text,
    signalTextColor: text,
    labelBoxBkgColor: fill,
    labelBoxBorderColor: border,
    labelTextColor: text,
    loopTextColor: muted,
    noteBkgColor: canvas,
    noteBorderColor: subtle,
    noteTextColor: text,
    activationBkgColor: fill,
    activationBorderColor: border,
    sequenceNumberColor: token('--color-text-on-action', '#ffffff'),

    errorBkgColor: canvas,
    errorTextColor: danger,
  };
}

/**
 * Subscribes to the `data-theme` attribute rather than to ThemeContext: this
 * component is also reachable from surfaces that render outside the provider
 * (the source panel preview), and the attribute is the single source of truth
 * ThemeContext writes to anyway.
 */
function useResolvedTheme() {
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(() => setTheme(readTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

export function MermaidDiagram({ chart, language = 'mermaid' }) {
  const { t } = useTranslation();
  const theme = useResolvedTheme();
  const reactId = useId();
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const containerRef = useRef(null);

  // `useId` contains colons, which are not valid in the CSS selectors mermaid
  // builds from the id it is handed.
  const renderId = useMemo(() => `etch-mermaid-${reactId.replace(/[^a-zA-Z0-9]/g, '')}`, [reactId]);

  useEffect(() => {
    let cancelled = false;
    const source = (chart || '').trim();
    if (!source) return undefined;

    setFailed(false);

    loadMermaid()
      .then(async (mermaid) => {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          themeVariables: themeVariables(),
          flowchart: { htmlLabels: false, useMaxWidth: true },
          sequence: { useMaxWidth: true },
          class: { htmlLabels: false, useMaxWidth: true },
          er: { useMaxWidth: true },
          gantt: { useMaxWidth: true },
        });

        // Validate before rendering: `parse` throws on invalid syntax without
        // leaving a half-built error graphic in the DOM the way `render` does.
        await mermaid.parse(source);
        const { svg: rendered } = await mermaid.render(renderId, source);
        if (!cancelled) setSvg(rendered);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setSvg('');
        }
      });

    return () => {
      cancelled = true;
      // mermaid parks a measuring node on <body> keyed by the render id.
      document.getElementById(`d${renderId}`)?.remove();
    };
  }, [chart, theme, renderId]);

  if (failed || !chart?.trim()) {
    return <CodeBlock code={chart || ''} language={language} />;
  }

  if (!svg) {
    return (
      <div className="etch-mermaid" data-state="loading">
        <p className="etch-visually-hidden" role="status">{t('states.loading')}</p>
        <pre className="etch-mermaid__placeholder" aria-hidden="true">{chart}</pre>
      </div>
    );
  }

  return (
    <figure className="etch-mermaid" data-state="ready">
      <div
        ref={containerRef}
        className="etch-mermaid__canvas"
        role="img"
        aria-label={t('chat.diagramLabel')}
        tabIndex={0}
        // The SVG comes from mermaid's own sanitiser (securityLevel: 'strict'),
        // which is the supported way to mount a rendered diagram.
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      <figcaption className="etch-mermaid__caption">
        <button
          type="button"
          className="etch-mermaid__toggle"
          aria-expanded={showSource}
          onClick={() => setShowSource((open) => !open)}
        >
          {showSource ? t('chat.diagramHideSource') : t('chat.diagramShowSource')}
        </button>
      </figcaption>
      {showSource && <CodeBlock code={chart} language={language} />}
    </figure>
  );
}

export default MermaidDiagram;
